import React, {
  useState,
  CSSProperties,
  memo,
  useCallback,
  useEffect,
} from "react";
import BasicDataTable from "../../../Component/BasicComponents/BasicDataTable";
import CustomInput from "../../../Component/BasicComponents/CustomInput";
import RouteModal from "../../../Component/BasicComponents/Modal/RouteModal";
import { Btn } from "../../../AbstractElements";
import { FaEdit, FaEye, FaInfoCircle, FaPlus } from "react-icons/fa";
import { RiPencilFill } from "react-icons/ri";
import {
  useAddGradeMaster,
  useBudgetsData,
  useGradeMastersData,
  useUpdateGradeMaster,
} from "./hooks/useGradeMaster";
import ReusableDropdown from "../../../Component/BasicComponents/Dropdowns/RouteDropdown";
import { useNavigate } from "react-router-dom";
import { AlignJustify } from "react-feather";
import { UncontrolledTooltip } from "reactstrap";

type Props = {};
interface TableButtonProps {
  onClick: () => void;
  title?: string;
  style?: CSSProperties;
}

const TableButton: React.FC<TableButtonProps> = memo(
  ({ onClick, title = "Add Heads", style }) => {
    const defaultStyle: CSSProperties = {
      display: "flex",
      borderRadius: "20px",
      padding: "2px",
      width: "110px",
      height: "40px",
      justifyContent: "center",
      textAlign: "center",
      alignItems: "center",
    };

    return (
      <div>
        <button
          onClick={onClick}
          className="btn btn-info"
          style={{ ...defaultStyle, ...style }} // External styles override default
        >
          <FaPlus /> <span style={{ marginLeft: "5px" }}>{title}</span>
        </button>
      </div>
    );
  }
);

const ExpenseTable = (props: Props) => {
  // Other Imports
  const navigate = useNavigate();

  //States
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    gradeName: "",
    selectedBudgetsUnit: [] as { id: number; label: string }[],
  });
  const [editForm, setEditForm] = useState("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  // API Hooks
  const { data } = useGradeMastersData({ enable: true });
  const { data: budgetsData } = useBudgetsData({ enable: true });
  const { mutate } = useAddGradeMaster();
  const { mutate: updateGradeMaster } = useUpdateGradeMaster();

  // Formatted Data For The MulitDropDown Budget
  let budgetData =
    budgetsData?.data.map((item: any) => ({
      id: item?.Bgid.toString(),
      label: item?.GroupName,
    })) || [];

  // Formatted Data For The Table
  let formattedData = data?.data.map((item: any, index: any) => ({
    ...item,
    Sr: index + 1,
  }));

  // Add Button Function
  const handleGrandeMasterTableButton = () => {
    setIsModelOpen(!isModelOpen);
    setEditForm("Add");
  };

  // Toggle Button For Modal
  const toggleModel = () => {
    setIsModelOpen(!isModelOpen);
    setEditForm("");
    setFormValues({
      gradeName: "",
      selectedBudgetsUnit: [] as { id: number; label: string }[],
    });
  };

  // Handle Close
  const handleClose = () => {
    setEditForm("");
    setIsModelOpen(false);
    setFormValues({
      gradeName: "",
      selectedBudgetsUnit: [] as { id: number; label: string }[],
    });
  };

  // Edit Button Function
  const handleGrandeMasterRequestEdit = (row: any) => {
    setIsModelOpen(true);
    setEditForm("Edit");
    setSelectedRowData(row);
  };

  const handleSubmitFormAddGrade = (e: React.FormEvent) => {
    e.preventDefault();

    const linkedBudget = formValues?.selectedBudgetsUnit.map(
      (item: any, index: any) => Number(item?.id)
    );

    const addBody = {
      gradeName: formValues?.gradeName || "",
      linkedBudgets: linkedBudget || [],
    };

    const updateBody = {
      gradeId: selectedRowData?.Gradeid || null,
      gradeName: formValues?.gradeName || "",
      linkedBudgets: linkedBudget || [],
    };

    if (editForm === "Add") {
      mutate(addBody, {
        onSuccess: () => {
          handleClose();
          setEditForm("");
          setSelectedRowData(null);
          setFormValues({
            gradeName: "",
            selectedBudgetsUnit: [] as { id: number; label: string }[],
          });
        },
      });
    } else {
      updateGradeMaster(updateBody, {
        onSuccess: () => {
          handleClose();
          setEditForm("");
          setSelectedRowData(null);
          setFormValues({
            gradeName: "",
            selectedBudgetsUnit: [] as { id: number; label: string }[],
          });
        },
      });
    }
  };

  // Handle input changes
  const handleInputChange = (
    name: any,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    // Convert specific fields to numbers
    const numericFields = ["adminApproval", "attachment", "leavePoints"];
    const updatedValue = numericFields.includes(name)
      ? value.replace(/[^0-9]/g, "") // Allow only numbers
      : value; // Keep string values as they are

    setFormValues((prevState) => ({
      ...prevState,
      [name]: numericFields.includes(name)
        ? updatedValue === ""
          ? ""
          : Number(updatedValue)
        : updatedValue,
    }));
  };

  // Add Functionality for Multiple Dropdown
  const handleMultiSelectChange = (selectedValues: string[] | number[]) => {
    const selectedData = selectedValues
      .map((id) =>
        budgetsData?.data.find(
          (unit: any) => unit.Bgid.toString() === id.toString()
        )
      )
      .map((item: any) => ({
        id: item.Bgid.toString(),
        label: item.GroupName,
      }));

    setFormValues((prevState) => ({
      ...prevState,
      selectedBudgetsUnit: selectedData.filter(Boolean), // Filter out undefined values
    }));
  };

  const onNameClick = (row: any) => {
    console.log("🚀 ~ ExpenseTable.tsx:191 ~ onNameClick ~ row:", row);

    navigate("/configuration/grademaster/gradepolicy", {
      state: { gradeId: row?.Gradeid },
    });
  };

  useEffect(() => {
    if (editForm === "Edit" && selectedRowData) {
      const selectedOrgUnits = selectedRowData?.linkedBudgets
        .split(",")
        .map((id: string) => id.trim())
        .map((id: string) => {
          const matchedUnit = budgetData?.find(
            (unit: any) => unit?.id.toString() === id.toString()
          );

          return matchedUnit ? { id: id, name: matchedUnit.label } : null;
        })
        .filter(Boolean);

      setFormValues({
        gradeName: selectedRowData?.GradeName || "",
        selectedBudgetsUnit: selectedOrgUnits || [],
      });
    } else {
      setFormValues({
        gradeName: "",
        selectedBudgetsUnit: [],
      });
    }
  }, [selectedRowData]);

  return (
    <>
      <div className="row mb-0 ">
        <div className="col-md-12 ">
          <BasicDataTable
            Title="Grade Master"
            data={formattedData || []}
            columns={GradeMasterDataColumns(
              handleGrandeMasterRequestEdit,
              onNameClick
            )}
            // tableHeaderButton={true}
            headerContent={
              <TableButton
                onClick={handleGrandeMasterTableButton}
                title="Add Grade"
                style={{ marginRight: "10px" }}
              />
            }
          />
        </div>
      </div>
      <RouteModal
        size="md"
        isOpen={isModelOpen}
        toggle={toggleModel}
        title={editForm === "Edit" ? "Edit Grade Master" : "Add Grade Master"}
        content={
          <form onSubmit={handleSubmitFormAddGrade}>

            <div className="row mb-0">
              <div className="col-md-12">
                <CustomInput
                  required
                  asterisk
                  label="Grade Name"
                  value={formValues.gradeName}
                  name="gradeName"
                  onChange={(e) => handleInputChange("gradeName", e)}
                  style={{ borderRadius: "4px" }}
                />
              </div>

              <div className="col-md-12 ">
                <ReusableDropdown
                  options={budgetData || []}
                  isMulti={true}
                  value={formValues?.selectedBudgetsUnit.map((unit) =>
                    unit?.id.toString()
                  )}
                  onChange={(value: any) => {
                    handleMultiSelectChange(value);
                  }}
                  DropdownHeading="Budgets"
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex justify-content-start gap-2 mt-4 ">
              <Btn
                style={{ backgroundColor: "#43B9B2", color: "white" }}
                type="submit"
              >
                {editForm === "Edit" ? "Update" : "Submit"}
              </Btn>
              <Btn
                style={{
                  backgroundColor: "#F9FAFC",
                  color: "#000",
                }}
                type="button"
                onClick={handleClose}
              >
                {" "}
                Cancel
              </Btn>
            </div>
          </form>
        }
      />
    </>
  );
};
export default ExpenseTable;

const GradeMasterDataColumns = (handleGrandeMasterRequestEdit: (row: any) => void, onNameClick: (row: any) => void
) => [
  {
    name: "Sr.",
    selector: (row: { Sr: number | string }) => row.Sr,
    // maxWidth: "40%",
  },
  {
    name: "Name",
    selector: (row: { GradeName: string }) => row.GradeName,
    // maxWidth: "40%",
  },
  {
    name: "Actions",
    // maxWidth: "20%",
    AlignJustify: "end",
    cell: (row: any) => (
      <div className="d-flex align-items-center">
        <button
          onClick={() => onNameClick(row)}
          className="btn btn-primary"
          style={{
            borderRadius: "20px",
            padding: "5px 10px 5px 10px",
            // width: "80px",
            marginRight: "5px",
          }}
        >
          <FaEye style={{ fill: "#fff" }} /> View Policy
        </button>
        <button
          onClick={() => handleGrandeMasterRequestEdit(row)}
          className="btn btn-primary"
          style={{ borderRadius: "20px", padding: "5px 10px 5px 10px" }}
        >
          <FaEdit style={{ fill: "#fff" }} /> Edit
        </button>
      </div>
    ),
  },
];
